import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { isEmpty, orderBy } from "lodash";
import matchSorter from "match-sorter";

import * as contractActions from "../../../../actions/contractActions";
import * as codeActions from "../../../../actions/codeActions";

import { formatDate } from "../../../../helpers/date";

import Loading from "../../../common/Loading";

import Filters from "../Filters";
import ITHealthScoreForm from "./ITHealthScoreForm";
import ITHealthScoreNotes from "./ITHealthScoreNotes";

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 }
];

const YEARS = ((back) => {
  const year = new Date().getFullYear();
  return Array.from({ length: back }, (v, i) => year - back + i + 1).map(
    (v) => ({
      label: v,
      value: v
    })
  );
})(10);

const getFilterValues = () => {
  const month = moment().subtract(1, "month").startOf("month").format("M");

  const year = formatDate(new Date(), "yyyy");

  return {
    month: { label: MONTHS[month - 1].label, value: month },
    year: { label: year, value: year }
  };
};

const INITIAL_FILTERS_STATE = {
  startMonth: getFilterValues().month,
  startYear: getFilterValues().year,
  contract: {}
};

const ITHealthScorePage = ({
  isMobile,
  actions,
  codes,
  contract,
  savingContract,
  contracts
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS_STATE);
  const [modifiedContracts, setModifiedContracts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChangeFilters = (field, input) => {
    setFilters((prev) => ({
      ...prev,
      [field]: input
    }));
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS_STATE);
  };

  const handleSubmit = async (item) => {
    setLoading(true);
    await actions.updateITHealthScore({
      ...item,
      month: filters.startMonth.value,
      year: filters.startYear.value
    });
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    actions.clearContract();
    setLoading(false);
  }, [actions]);

  useEffect(() => {
    const fetchCodes = async () => {
      await actions.loadCodes();
    };

    if (isEmpty(codes)) {
      setLoading(true);
      fetchCodes();
      setLoading(false);
    }
  }, [actions, codes]);

  useEffect(() => {
    const contractType = codes.find(
      (code) =>
        code.entity === "Contract" &&
        code.field === "type" &&
        code.label === "MANAGED SERVICES"
    );
    const contractStatus = codes.find(
      (code) =>
        code.entity === "Contract" &&
        code.field === "status" &&
        code.label === "Active"
    );
    if (contractType && contractStatus) {
      actions.loadContracts({
        filtered: {
          contract_type: [contractType.id],
          contract_status: [contractStatus.id]
        }
      });
    }
  }, [actions, codes]);

  useEffect(() => {
    setModifiedContracts(
      orderBy(contracts, ["customer.name", "name"])
        .filter((c) => c.tier?.normal_sla > 0)
        .map((c) => ({
          label: `${c.customer?.name} - ${c.name}`,
          value: c._id
        }))
    );
  }, [contracts]);

  const handleExportPDF = async () => {
    setLoading(true);
    const queryFilters = {
      filters
    };
    const payload = {
      data: queryFilters,
      type: "IT_MANAGED_SERVICES",
      format: "pdf"
    };
    await actions.performExport(payload);
    setLoading(false);
  };

  const handleFetchClick = async () => {
    if (!isEmpty(filters.contract)) {
      setLoading(true);
      await actions.loadITHealthScoreContract({
        filtered: {
          contract: filters.contract.value,
          period: [filters.startMonth.value, filters.startYear.value]
        }
      });
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <Row style={{ margin: 0 }}>
        <Col md={12}>
          <Filters
            onPDFExport={handleExportPDF}
            filters={{ ...filters, months: MONTHS, years: YEARS }}
            onChangeFilters={handleChangeFilters}
            onReset={resetFilters}
            onFilter={handleFetchClick}
            contracts={modifiedContracts}
            title={`IT Health Score - ${filters.startMonth.label} ${filters.startYear.label}`}
          />
        </Col>
      </Row>
      {loading && (
        <div className="loading">
          <Loading css={{ margin: "100px auto 0 auto" }} />
        </div>
      )}
      {!loading && !isEmpty(contract) && (
        <React.Fragment>
          <ITHealthScoreForm
            contract={contract}
            onSubmit={handleSubmit}
            savingContract={savingContract}
            month={filters.startMonth.value}
            year={filters.startYear.value}
          />
          <ITHealthScoreNotes
            notes={matchSorter(contract.notes, "IT Health Score Update", {
              keys: ["title"]
            })}
            isMobile={isMobile}
          />
        </React.Fragment>
      )}
      {!loading && isEmpty(contract) && (
        <div className="no-data">No Data Available</div>
      )}
    </React.Fragment>
  );
};

ITHealthScorePage.propTypes = {
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  contracts: PropTypes.array,
  codes: PropTypes.array,
  contract: PropTypes.object,
  savingContract: PropTypes.bool
};

ITHealthScorePage.defaultProps = {
  contracts: [],
  codes: [],
  contract: {},
  savingContract: false
};

function mapStatesToProps(state) {
  return {
    contracts: state.reducers.contracts,
    codes: state.reducers.codes,
    contract: state.reducers.contract,
    savingContract: state.reducers.savingContract
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...contractActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(ITHealthScorePage);
